import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Linkedin, Instagram, MessageCircle, ArrowRight } from "lucide-react";
import { SITE } from "@/constants/testIds";
import logoImg from "@/assets/moduleris-logo.jpeg";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "https://moduleris-api.isvecli.com").replace(/\/$/, "");
const API = `${BACKEND}/api`;

const quickLinks = [
  { label: "Hizmetler", href: "#services", external: false },
  { label: "Projeler", href: "#projects", external: false },
  { label: "Galeri", href: "#gallery", external: false },
  { label: "Kurumsal", href: "#about", external: false },
  { label: "İletişim", href: "#contact", external: false },
  { label: "KVKK Metni", href: "/kvkk", route: true },
  { label: "Çerez Politikası", href: "/cerez-politikasi", route: true },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOk(false);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setOk(true);
      toast.success("Bültenimize abone oldunuz!");
      setEmail("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kayıt yapılamadı.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#050506] pt-24 pb-10 border-t border-neutral-900 relative overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="relative inline-flex items-center justify-center w-11 h-11 bg-white/95 p-1">
                <img src={logoImg} alt="MoDuLerİS logo" className="w-full h-full object-contain" />
              </span>
              <span className="font-display font-semibold text-lg tracking-tight">
                MoDuLer<span className="text-[#ff5a00]">İ</span>S
              </span>
            </div>
            <p className="text-neutral-400 text-sm leading-relaxed max-w-sm">
              2012'den beri geleceği inşa ediyoruz. Enerji, elektrik, mimari ve restorasyonu tek çatı
              altında birleştiren modüler bir yaklaşım.
            </p>
            <div className="flex items-center gap-3 mt-8">
              <a
                data-testid={SITE.socialInstagram}
                href="https://instagram.com/moduleris26"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram — @moduleris26"
                className="w-10 h-10 flex items-center justify-center border border-neutral-800 hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                data-testid="social-whatsapp"
                href="https://wa.me/905456534494"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp Business"
                className="w-10 h-10 flex items-center justify-center border border-neutral-800 hover:border-[#25D366] hover:text-[#25D366] transition-colors"
              >
                <MessageCircle size={16} />
              </a>
              <a
                data-testid={SITE.socialLinkedin}
                href="#"
                aria-label="LinkedIn"
                className="w-10 h-10 flex items-center justify-center border border-neutral-800 hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <h4 className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">
              Hızlı Erişim
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((l) =>
                l.route ? (
                  <li key={l.label}>
                    <Link
                      to={l.href}
                      className="text-neutral-300 hover:text-[#ff5a00] transition-colors text-sm"
                    >
                      {l.label}
                    </Link>
                  </li>
                ) : (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="text-neutral-300 hover:text-[#ff5a00] transition-colors text-sm"
                    >
                      {l.label}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-5">
            <h4 className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">
              E-Bülten
            </h4>
            <p className="text-neutral-300 text-sm mb-5 max-w-md">
              Sektörel gelişmelerden ve projelerimizden haberdar olun.
            </p>
            <form onSubmit={submit} className="flex border border-neutral-800 focus-within:border-[#ff5a00] transition-colors">
              <input
                data-testid={SITE.newsletterEmail}
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e-posta adresiniz"
                className="flex-1 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-neutral-600"
              />
              <button
                data-testid={SITE.newsletterSubmit}
                type="submit"
                disabled={loading}
                className="btn-accent px-5 py-3 text-[11px] uppercase tracking-[0.24em] font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? "..." : "Abone Ol"} <ArrowRight size={14} />
              </button>
            </form>
            {ok && (
              <div
                data-testid={SITE.newsletterSuccess}
                className="mt-4 text-xs text-[#ff5a00] font-mono-tech uppercase tracking-[0.22em]"
              >
                ✓ Kaydınız alındı.
              </div>
            )}
          </div>
        </div>

        {/* Massive brand */}
        <div className="mt-20 pt-10 border-t border-neutral-900">
          <div className="font-display font-bold text-[16vw] leading-none tracking-tight text-neutral-900 select-none pointer-events-none">
            MoDuLerİS
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 font-mono-tech text-[10px] uppercase tracking-[0.28em] text-neutral-500">
          <div className="flex items-center gap-3 flex-wrap">
            <span>© 2026 MoDuLerİS<sup className="text-[8px] text-[#ff5a00] ml-0.5" title="Marka tescil süreci">®</sup>. Tüm Hakları Saklıdır.</span>
            <span className="hidden md:inline text-neutral-700">·</span>
            <span className="text-neutral-600">Marka Tescil Başvurusu: 2026</span>
          </div>
          <div>Geleceği İnşa Ediyoruz · Est. 2012 · İzmir / TR</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
